// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from 'Store';
import { orderStatusCode } from 'Utils/constants';

const selectProjectList = (state) => state.projectList.list;

const selectFolderProjectList = (state) => state.folderList.projectsInFolder;

const selectOrderSortFilter = (state: RootState) => state.projectList.orderSortFilter;
const selectOrderSortFilterTouched = (state: RootState) => state.projectList.orderSortFilterTouched;
const selectKeywordFilter = (state: RootState) => state.projectList.keywordFilter;

// "Unassigned Projects" is the dashboard's default filter, not a deliberate choice, so a search
// made without ever touching the dropdown should span every folder (searching doesn't feel like
// browsing). Once the user explicitly picks a filter - including re-picking "Unassigned
// Projects" - it's honored as-is, search or not. Used both to build the projects request and to
// decide what the filter dropdown displays, so the two never disagree.
// Selecting the three individual fields (rather than the whole projectList slice) keeps this
// from recomputing on unrelated slice updates like pagination or the loaded project list.
const selectEffectiveOrderSortFilter = createSelector(
  [selectOrderSortFilter, selectOrderSortFilterTouched, selectKeywordFilter],
  (orderSortFilter, orderSortFilterTouched, keywordFilter) => (
    (orderSortFilter === 'unassigned' && !orderSortFilterTouched && keywordFilter) ? 'all' : orderSortFilter
  ),
);

const selectProjectInfo = createSelector([selectProjectList, selectFolderProjectList, (state) => state.auth.user, (_, project, isInsideFolder) => ({ project, isInsideFolder })], (list, folderProjectList, user, { project, isInsideFolder }) => {
  const dashboardProjectFilter = isInsideFolder ? folderProjectList.filter((o) => o._id === project._id) : list.filter((o) => o._id === project._id);
  const [dashboardProject] = dashboardProjectFilter;
  if (!dashboardProject) {
    return {
      placed: 0,
      autonomous: 0,
      cleaning: 0,
      complete: 0,
    };
  }
  const orders = [...dashboardProject.orders];

  const dashboardProjectOrders = orders.sort((order1, order2) => (order1.createdAt > order2.createdAt ? 1 : -1));
  // TODO: use orderStatusCode constants instead of hardcoded values
  const orderFinalized = dashboardProjectOrders.some((o) => o.orderStatus === 7);

  const orderStatusResult = orders.reduce((result, order) => {
    if ([1, 2, 3, 4, 5, 6, 7, 8].includes(order.orderStatus)) {
      result[order.ownerProject] = order.orderStatus;
    }
    return result;
  }, {} as { [key: string]: number });

  const orderEstimatedDeliveryDates = orders.reduce((result, order) => {
    if (order?.deliveryEstimate) {
      result[order.ownerProject] = order.deliveryEstimate;
    }
    return result;
  }, {} as { [key: string]: Date });

  const orderDeliveredDates = orders.reduce((result, order) => {
    if (order?.deliveredDate) {
      result[order.ownerProject] = order.deliveredDate;
    }
    return result;
  }, {} as { [key: string]: Date });

  const orderEstimateResult = orders.filter((order) => order.isEstimate).map((order) => order.ownerProject);

  const expressAIReady = orders?.some((order) => order.cadFiles?.some((cadfile) => (cadfile.expressAIUpload && cadfile.expressAIEmailSent)));

  // Check if any of the orders in the project are being processed
  const projectProcessing = dashboardProjectOrders.some((order) => order.orderStatus > 0);
  // Check if project is created by the current user
  const isProjectCreatedByUser = project.owner === user._id;
  // Variable to check if a project can be deleted
  const cannotDelete = projectProcessing || !isProjectCreatedByUser;
  const orderStatus = orderStatusResult[project._id];
  const orderEstimate = orderEstimateResult.includes(project._id);
  const orderEstimatedDeliveryDate = orderEstimatedDeliveryDates[project._id] || null;
  const orderDeliveredOn = orderDeliveredDates[project._id] || null;
  const { imageryRequest, rasterRequest } = project;
  return {
    isInProgress: orderStatus && (orderStatus > orderStatusCode.created && orderStatus < orderStatusCode.manualDrafting),
    placed: orderStatus && (orderStatus === orderStatusCode.processing || orderStatus === orderStatusCode.autonomousFailed) ? 1 : 0,
    autonomous: orderStatus && orderStatus === orderStatusCode.autonomousSuccessful ? 2 : 0,
    cleaning: orderStatus && (orderStatus === orderStatusCode.manualDrafting || orderStatus === orderStatusCode.usQc || orderStatus === orderStatusCode.qcComplete) ? 3 : 0,
    complete: orderStatus && orderStatus === orderStatusCode.done ? 3 : 0,
    cannotDelete,
    orderFinalized,
    expressAIReady,
    orderEstimate,
    orderStatus,
    imageryRequest,
    rasterRequest,
    orderEstimatedDeliveryDate,
    orderDeliveredOn,
  };
});

const selectMapCoordinates = (state) => state.projectList.mapCoordinates;

// The dedicated coordinates fetch covers every project matching the current search/filter.
// Deliberately NO grid-list fallback: the pin card lookup, the rail, the summary panel, and
// the empty state all read mapCoordinates, so fallback pins would be unclickable dead ends
// under UI claiming nothing exists.
const selectMarkerSourceProjects = selectMapCoordinates;

// Deliberately independent of the hovered project id: this array becomes the map's GeoJSON
// source, and rebuilding it re-uploads and re-clusters the whole source.
const selectAllCoordinates = createSelector(
  [selectMarkerSourceProjects],
  (projects) => {
    const coordinatesInfo = projects
      .filter((project) => project.middlePoint)
      .map((p) => ({
        _id: p._id,
        coords: [p.middlePoint.longitude, p.middlePoint.latitude],
      }));
    return coordinatesInfo;
  },
);

// The full coordinate entry for the pin whose overview card is open - undefined once a search
// or filter change stops plotting that project, which closes the card.
const selectSelectedPinProject = createSelector(
  [
    (state) => state.projectList.mapCoordinates,
    (state) => state.projectList.selectedPinProjectId,
  ],
  (mapCoordinates: IProjectMapCoordinate[], selectedPinProjectId: string | null) => (selectedPinProjectId
    ? mapCoordinates.find((coordinate) => coordinate._id === selectedPinProjectId)
    : undefined),
);

export { selectProjectInfo, selectAllCoordinates, selectEffectiveOrderSortFilter, selectSelectedPinProject };
